/**
 * 🎯 시세 조회 전용 등급별 가격 표시 컴포넌트
 * 
 * [배경]
 * - 기존 "단가표 보기" 기능을 "시세 조회"로 개선
 * - 모델/용량 선택 후 등급별 가격을 조회 전용으로 표시
 * - "바로 접수하기" 버튼으로 접수 페이지 연결
 * 
 * [UI 요소]
 * - 선택된 모델의 대표 이미지 표시
 * - A/B/C/D 등급별 가격표 전체 노출
 * - 최저가 ~ 최고가 범위 표시
 * - 등급 기준 자세히 보기 버튼
 * - "바로 접수하기" 버튼 (애니메이션, 강조 UI)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChannelPath } from '../../contexts/ChannelContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  InfoOutlined,
  PriceCheckRounded,
  Info,
  RocketLaunch,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { DeviceGrade } from '../../types/gradeSystem';
import { resolvePhoneImage, BRAND_DEFAULT_PHONE_IMAGE } from './AddDeviceDialog/utils';
import { getGradesForCategory } from '../../constants/grades';
import GradeCriteriaGuideDialog, { GRADE_INFO } from './common/GradeCriteriaGuideDialog';

interface PriceQuoteGradeDisplayProps {
  // 선택된 기기 정보
  deviceType: string;
  deviceModel: string;
  deviceStorage: string;
  deviceImage?: string;
  connectivity?: string;
  
  // 등급별 가격 정보
  gradePrices: { A: number; B: number; C: number; D: number; E: number } | null;
  
  // 로딩 상태
  loading?: boolean;
  
  // 에러 메시지
  error?: string | null;
  
  // 다이얼로그 닫기 콜백 (바로 접수하기 클릭 시 사용)
  onClose?: () => void;
}

const PriceQuoteGradeDisplay: React.FC<PriceQuoteGradeDisplayProps> = ({
  deviceType,
  deviceModel,
  deviceStorage,
  deviceImage,
  connectivity,
  gradePrices,
  loading = false,
  error = null,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const channelPath = useChannelPath();
  const [showCriteriaDialog, setShowCriteriaDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const grades: DeviceGrade[] = getGradesForCategory(deviceType) as DeviceGrade[];

  // 가격 데이터가 있는지 확인
  const hasValidPrices = gradePrices && Object.values(gradePrices).some(price => price > 0);
  
  // 최고/최저 가격 계산
  const validPrices = hasValidPrices 
    ? grades.map(grade => gradePrices[grade]).filter(price => price > 0)
    : [];
  const maxPrice = validPrices.length > 0 ? Math.max(...validPrices) : 0;
  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const priceRange = maxPrice - minPrice;

  // 🔧 이미지 폴백 처리 함수 — 스마트폰은 매니페스트 리졸버(실존 파일 보장)로 위임
  const getDeviceImage = (): string => {
    // 이미지 에러가 발생하지 않고, deviceImage가 유효하면 그대로 사용
    if (!imageError && deviceImage && deviceImage.trim() && !deviceImage.includes('undefined')) {
      return deviceImage;
    }

    const modelLower = deviceModel.toLowerCase();

    // iPad/아이패드
    if (modelLower.includes('ipad') || deviceModel.includes('아이패드')) {
      return resolvePhoneImage({ brand: 'Apple', modelKo: deviceModel, catalogImage: deviceImage });
    }

    // Galaxy Tab (태블릿) — 기존 S8 고정 이미지 대신 모델별 리졸버 (미보유 시 태블릿 기본 이미지)
    if ((modelLower.includes('galaxy') && modelLower.includes('tab')) || deviceModel.includes('갤럭시 탭')) {
      return resolvePhoneImage({ brand: 'Samsung', modelKo: deviceModel, catalogImage: deviceImage });
    }

    // iPhone/아이폰 (스마트폰)
    if (modelLower.includes('iphone') || deviceModel.includes('아이폰')) {
      return resolvePhoneImage({ brand: 'Apple', modelKo: deviceModel, catalogImage: deviceImage });
    }

    // Galaxy 스마트폰 (S/Z/Note/A/J)
    if (modelLower.includes('galaxy') || deviceModel.includes('갤럭시')) {
      return resolvePhoneImage({ brand: 'Samsung', modelKo: deviceModel, catalogImage: deviceImage });
    }

    // 웨어러블 (AirPods 등) — 실존 파일로 교정 (기존 /assets/airpods/default.png는 미존재 경로였음)
    if (modelLower.includes('airpod') || modelLower.includes('에어팟')) {
      return '/assets/airPot/에어팟3세대.png';
    }

    return BRAND_DEFAULT_PHONE_IMAGE;
  };

  // 이미지 로드 체크 (컴포넌트 마운트 시)
  useEffect(() => {
    setImageError(false);
  }, [deviceImage, deviceModel]);

  // 🎯 바로 접수하기 클릭 핸들러
  const handleQuickSubmit = () => {
    // sessionStorage에 시세조회 기기 정보 저장 (최소 데이터만)
    const priceQuoteDevice = {
      deviceType: deviceType || '스마트폰',
      deviceModel: deviceStorage 
        ? `${deviceModel} - ${deviceStorage}${connectivity ? ` - ${connectivity}` : ''}`
        : deviceModel,
      deviceImage: getDeviceImage(),
      priceRange: { min: minPrice, max: maxPrice },
      timestamp: Date.now(),
    };
    
    sessionStorage.setItem('priceQuoteDevice', JSON.stringify(priceQuoteDevice));
    
    // 다이얼로그 닫기
    if (onClose) {
      onClose();
    }
    
    // 접수 페이지로 이동 (채널 경로 자동 적용)
    navigate(channelPath('/apply'));
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* 상단 헤더 */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PriceCheckRounded sx={{ color: 'primary.main', fontSize: 28 }} />
              <Box>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700 }}>
                  시세 조회 결과
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  선택하신 기기의 등급별 예상 매입가입니다
                </Typography>
              </Box>
            </Box>
            
            {/* 🎯 등급 기준 자세히 보기 버튼 */}
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<Info />}
              onClick={() => setShowCriteriaDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                alignSelf: { xs: 'flex-end', sm: 'center' },
                whiteSpace: 'nowrap',
              }}
            >
              등급 기준 자세히 보기
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 🎯 선택된 기기 정보 + 시세 + 바로 접수하기 통합 카드 */}
      <Card 
        sx={{ 
          mb: 2, 
          borderRadius: 2, 
          boxShadow: '0 4px 16px rgba(99, 102, 241, 0.12)',
          border: '2px solid rgba(99, 102, 241, 0.15)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Grid container spacing={2} alignItems="center">
            {/* 왼쪽: 기기 이미지 + 정보 */}
            <Grid item xs={12} sm={5} md={4}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'row', sm: 'row' },
                alignItems: 'center',
                gap: 2,
              }}>
                {/* 기기 대표 이미지 */}
                <Box
                  component="img"
                  src={getDeviceImage()}
                  alt={deviceModel}
                  sx={{
                    width: { xs: 70, sm: 80 },
                    height: { xs: 70, sm: 80 },
                    objectFit: 'contain',
                    borderRadius: 2,
                    backgroundColor: 'grey.100',
                    p: 1,
                    flexShrink: 0,
                  }}
                  onError={() => setImageError(true)}
                />
                
                {/* 기기 정보 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}
                    noWrap={!isMobile}
                  >
                    {deviceModel}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {deviceStorage && (
                      <Chip 
                        label={deviceStorage} 
                        size="small" 
                        sx={{ 
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600, 
                          backgroundColor: 'primary.main', 
                          color: 'white' 
                        }}
                      />
                    )}
                    {connectivity && (
                      <Chip 
                        label={connectivity} 
                        size="small" 
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* 중앙: 시세 범위 (PC) / 아래로 (모바일) */}
            {!loading && hasValidPrices && (
              <Grid item xs={12} sm={4} md={4}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  py: { xs: 1, sm: 0 },
                  borderTop: { xs: '1px solid', sm: 'none' },
                  borderBottom: { xs: '1px solid', sm: 'none' },
                  borderColor: 'divider',
                }}>
                  {/* 최고가 */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      최고가
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ fontWeight: 800, color: GRADE_INFO.A.color, lineHeight: 1.2 }}
                    >
                      {maxPrice.toLocaleString()}원
                    </Typography>
                  </Box>

                  <Typography variant="h6" color="text.disabled" sx={{ fontWeight: 300 }}>~</Typography>

                  {/* 최저가 */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      최저가
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ fontWeight: 800, color: GRADE_INFO.D.color, lineHeight: 1.2 }}
                    >
                      {minPrice.toLocaleString()}원
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* 오른쪽: 바로 접수하기 버튼 - 가격 정보 유무와 관계없이 항상 표시 */}
            {!loading && (
              <Grid item xs={12} sm={hasValidPrices ? 3 : 7} md={hasValidPrices ? 4 : 8}>
                <Box
                  component={motion.div}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleQuickSubmit}
                    startIcon={<RocketLaunch sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                      py: { xs: 1.2, sm: 1.5 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 800,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'shimmer 2s infinite',
                      },
                      '@keyframes shimmer': {
                        '0%': { left: '-100%' },
                        '100%': { left: '100%' },
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #9333ea 100%)',
                        boxShadow: '0 6px 16px rgba(99, 102, 241, 0.45)',
                      },
                    }}
                  >
                    🚀 바로 접수
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* 등급별 가격 표시 */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chip 
              label="등급별 시세" 
              color="primary" 
              sx={{ mr: 2, fontWeight: 600, height: { xs: 24, sm: 28 } }} 
            />
            <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 600 }}>
              예상 매입 가격
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* 로딩 상태 */}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ mr: 2 }} />
              <Typography variant="body1" color="text.secondary">
                시세 정보를 불러오는 중...
              </Typography>
            </Box>
          )}

          {/* 에러 상태 */}
          {error && !loading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 가격 정보 없음 */}
          {!loading && !error && !hasValidPrices && (
            <Alert severity="info" sx={{ mb: 2 }}>
              해당 기기의 시세 정보가 등록되지 않았습니다. 고객센터로 문의해주세요.
            </Alert>
          )}

          {/* 등급별 가격 카드 그리드 */}
          {/* ⚠️ [UI 개선] 카드 높이 동일하게 맞춤 - height: '100%' + display: 'flex' 적용 */}
          {!loading && hasValidPrices && (
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
              {grades.map((grade) => {
                const price = gradePrices[grade];
                const gradeInfo = GRADE_INFO[grade];
                const percentage = priceRange > 0 ? ((price - minPrice) / priceRange) * 100 : 50;
                const isHighest = price === maxPrice;
                const isLowest = price === minPrice;

                return (
                  <Grid item xs={6} sm={3} key={grade} sx={{ display: 'flex' }}>
                    <Card
                      sx={{
                        border: `2px solid ${gradeInfo.color}${isHighest || isLowest ? '60' : '20'}`,
                        backgroundColor: `${gradeInfo.color}${isHighest || isLowest ? '10' : '05'}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'default',
                        transition: 'none',
                        width: '100%', // 전체 너비 사용
                        height: '100%', // 동일한 높이 유지
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* 최고가/최저가 배지 */}
                      {(isHighest || isLowest) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: isHighest ? GRADE_INFO.A.color : GRADE_INFO.D.color,
                            color: 'white',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {isHighest ? '최고가' : '최저가'}
                        </Box>
                      )}

                      <CardContent 
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1, // 남은 공간 채우기
                          justifyContent: 'space-between', // 내용물 균등 배치
                        }}
                      >
                        {/* 상단 영역: 등급 라벨 + 가격 */}
                        <Box>
                          {/* 등급 라벨 */}
                          <Chip
                            label={gradeInfo.label}
                            size="small"
                            sx={{
                              backgroundColor: gradeInfo.color,
                              color: 'white',
                              fontWeight: 700,
                              mb: { xs: 1, sm: 1.5 },
                              width: '100%',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            }}
                          />

                          {/* 가격 */}
                          <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            sx={{
                              fontWeight: 700,
                              color: gradeInfo.color,
                              fontFamily: 'tabular-nums',
                              mb: { xs: 0.5, sm: 1 },
                              fontSize: { xs: '1rem', sm: '1.15rem' },
                            }}
                          >
                            {price > 0 ? `${price.toLocaleString()}원` : '-'}
                          </Typography>
                        </Box>

                        {/* 하단 영역: 가격바 + 설명 */}
                        <Box>
                          {/* 가격 바 (시각적 비교용) */}
                          {price > 0 && (
                            <Box
                              sx={{
                                height: 4,
                                backgroundColor: '#e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                                position: 'relative',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${percentage}%`,
                                  backgroundColor: gradeInfo.color,
                                  borderRadius: 2,
                                }}
                              />
                            </Box>
                          )}

                          {/* 등급 설명 (모바일에서는 숨김) - 고정 높이로 통일 */}
                          {!isMobile && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ 
                                display: 'block', 
                                mt: 1, 
                                fontSize: '0.7rem',
                                lineHeight: 1.3,
                                minHeight: '2.6em', // 2줄 높이 고정 (텍스트 길이 차이로 인한 높이 변동 방지)
                                overflow: 'hidden',
                              }}
                            >
                              {gradeInfo.description}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* 하단 안내 문구 */}
          <Divider sx={{ my: 2 }} />
          
          <Alert 
            severity="info" 
            icon={<InfoOutlined />}
            sx={{ 
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              '& .MuiAlert-icon': { color: '#856404' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ⚠️ 실제 검수 결과에 따라 최종 등급과 금액이 달라질 수 있습니다.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* 등급 기준 자세히 보기 모달 */}
      <GradeCriteriaGuideDialog
        open={showCriteriaDialog}
        onClose={() => setShowCriteriaDialog(false)}
        deviceType={deviceType}
      />
    </Box>
  );
};

export default PriceQuoteGradeDisplay;
